import React, { useContext, useEffect, useState } from 'react';
import { EllipsisOutlined, ExclamationCircleOutlined, PlusOutlined, ReloadOutlined, SafetyOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { NewRolePage } from './new-role-page';
import { NavLink } from 'react-router-dom';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { Role } from '../../../models';
import { TableNameColumn } from '../../../elements';
import { EntityChangeLog } from '../../reports';
import { UserContext } from '../../contexts';

export function RolesPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<(Role & { key: string })[]>();
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    setCanEdit(hasPermission(user, 'roles.edit'));
  }, [user]);

  const loadRoles = () => {
    setLoading(true);
    apiClient
      .get('roles')
      .then(response => {
        const responseRoles: Role[] = response.data;
        const responseData = responseRoles.map(role => {
          return {
            key: role.id,
            ...role,
          };
        });
        setRoles(responseData);
        setLoading(false);
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const openAddModal = () => {
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const onRefreshClick = () => {
    loadRoles();
  };

  const onRoleAdded = () => {
    closeAddModal();
    loadRoles();
  };

  const deleteRole = (id: string) => {
    setLoading(true);
    apiClient
      .delete(`roles/${id}`)
      .then(() => {
        setLoading(false);
        loadRoles();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const confirmDeleteRole = (id: string) => {
    const onOk = () => deleteRole(id);
    Modal.confirm({
      title: 'Rol sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Rolü silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    } else if (e.key === 'del') {
      confirmDeleteRole(id);
    }
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      {canEdit && (
        <Menu.Item key="edit">
          <NavLink to={'/admin/roles/' + id}>Düzenle</NavLink>
        </Menu.Item>
      )}
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
      {canEdit && (
        <Menu.Item key="del">
          <Text type="danger">Sil</Text>
        </Menu.Item>
      )}
    </Menu>
  );

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, role: Role) => <TableNameColumn name={name} description={role.description} path={'/admin/roles/' + role.id} />,
    },
    {
      title: 'Yetkiler',
      dataIndex: 'numRolePermissions',
      key: 'numRolePermissions',
      render: (num: number) => (num === 0 ? '-' : num),
    },
    {
      title: 'Kullanıcılar',
      dataIndex: 'numUserRoles',
      key: 'numUserRoles',
      render: (num: number) => (num === 0 ? '-' : num),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, role: Role) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(role.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <SafetyOutlined /> Roller
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <Button key="add" onClick={openAddModal} icon={<PlusOutlined />} type="primary">
                Yeni rol
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={roles} loading={loading} pagination={false} />
      </Content>
      <Modal title="Yeni rol" visible={addModalOpen} footer={null} onCancel={closeAddModal} destroyOnClose={true}>
        <NewRolePage onSaved={onRoleAdded} />
      </Modal>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'Role'} recordId={changeLogModalOpen} />
      </Modal>
    </>
  );
}
